import React, { useContext, useEffect, useRef, useState } from "react";
import { Box, Button, Divider, Typography } from "@mui/material";
import PageHeader from "@bytenite/components/src/components/layoutComponents/PageHeader";
import "@bytenite/components/src/styles/sass/containers.scss";
import JobHistory from "@bytenite/components/src/components/displayDataComponents/JobHistory";
import { withApi } from "../hoc/ApiProvider/Provider";
import { useNavigate } from "react-router-dom";
import AuthContext from "@bytenite/auth/src/hoc/Auth/context";
import { CommonFilterCondition, JobJobState } from "../client";
import apiService from "../services/ApiService";

export const POLLING_INTERVAL = 30000;

//function taken from https://blog.bitsrc.io/polling-in-react-using-the-useinterval-custom-hook-e2bcefda4197
export function useInterval(callback, delay) {
  const savedCallback = useRef();
  // Remember the latest callback
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval
  useEffect(() => {
    function tick() {
      savedCallback.current();
    }
    if (delay !== null) {
      const id = setInterval(tick, delay);
      return () => {
        clearInterval(id);
      };
    }
  }, [callback, delay]);
}

const Encoding = (props) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingJobDetails, setIsLoadingJobDetails] = useState(true);
  const [jobsHistoryFilters, setJobsHistoryFilters] = useState({
    limit: 5,
    offset: 0,
    filters: [
      {
        field: "state",
        condition: "FILTER_CONDITION_IN",
        value: {
          "@type": "type.googleapis.com/google.protobuf.Value",
          value: ["JOB_STATE_COMPLETE", "JOB_STATE_FAILED"],
        },
      },
    ],
    orderBy:
      "CASE WHEN ended_at IS NULL THEN 1 ELSE 0 END, ended_at DESC, updated_at DESC",
  });
  const [jobsQueueFilters, setJobsQueueFilters] = useState({
    limit: 5,
    offset: 0,
    filters: [
      {
        field: "state",
        condition: "FILTER_CONDITION_NOT_IN",
        value: {
          "@type": "type.googleapis.com/google.protobuf.Value",
          value: ["JOB_STATE_COMPLETE", "JOB_STATE_FAILED"],
        },
      },
    ],
    orderBy:
      "CASE WHEN started_at IS NULL THEN 1 ELSE 0 END, started_at DESC, created_at DESC",
  });

  const [jobsHistory, setJobsHistory] = useState({});
  const [jobsQueue, setJobsQueue] = useState({});
  const [runningJobsTasks, setRunningJobsTasks] = useState({});
  const [jobHistoryDetails, setJobHistoryDetails] = useState({});
  const [jobQueueDetails, setJobQueueDetails] = useState({});
  const [resultData, setResultData] = useState({});

  const loadJobsHistory = (api) => {
    return api
      .getJobsFiltered(jobsHistoryFilters)
      .then((resp) => {
        setJobsHistory(resp);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setIsLoading(false);
        setJobsHistory([]);
      });
  };

  const loadQueueHistory = (api) => {
    api
      .getJobsFiltered(jobsQueueFilters)
      .then((resp) => {
        setIsLoading(false);
        setJobsQueue(resp);
      })
      .catch((err) => {
        console.error(err);
        setIsLoading(false);
        setJobsQueue([]);
      });
  };

  const abortJob = (api, jobId) => {
    setIsLoading(true);
    api.abortJob(jobId).then(() => {
      loadJobsHistory(api);
      loadQueueHistory(api);
    });
  };

  const copyJob = (api, jobId) => {
    setIsLoading(true);
    api.copyJob(jobId).then((res) => {
      loadQueueHistory(api);
      loadJobsHistory(api);
    });
  };

  const deleteJob = (api, jobId) => {
    setIsLoading(true);
    api.deleteJob(jobId).then(() => {
      loadJobsHistory(api);
      loadQueueHistory(api);
    });
  };

  const runJob = (api, jobId) => {
    setIsLoading(true);
    api.runJob(jobId).then(() => {
      loadJobsHistory(api);
      loadQueueHistory(api);
    });
  };

  const downloadResults = (jobId) => {
    props.api.getJobResults(jobId).then((results) => {
      if (results) {
        setResultData(results);
      }
    });
  };

  const canAbort = (jobState) => {
    return (
      [
        JobJobState.UPLOADING,
        JobJobState.PARTITIONING,
        JobJobState.PARTITIONED,
        JobJobState.DOWNLOADING,
        JobJobState.MERGED,
        JobJobState.MERGING,
        JobJobState.PARTITIONED,
        JobJobState.PARTITIONING,
        JobJobState.QUEUED,
        JobJobState.TASKS_COMPLETED,
      ].indexOf(jobState) !== -1
    );
  };
  const canDelete = (jobState) => {
    return (
      [JobJobState.NEW, JobJobState.FAILED, JobJobState.COMPLETE].indexOf(
        jobState
      ) !== -1
    );
  };
  const canEdit = (jobState) => {
    return [JobJobState.NEW].indexOf(jobState) !== -1;
  };
  const isComplete = (jobState) => {
    return [JobJobState.FAILED, JobJobState.COMPLETE].indexOf(jobState) !== -1;
  };

  const addFilterPageNumHistory = (pageNum) => {
    let filters = jobsHistoryFilters;
    filters.offset = (pageNum - 1) * filters.limit;
    setJobsHistoryFilters(JSON.parse(JSON.stringify(filters)));
  };

  const addFilterPageNumQueue = (pageNum) => {
    let filters = jobsQueueFilters;
    filters.offset = (pageNum - 1) * filters.limit;
    setJobsQueueFilters(JSON.parse(JSON.stringify(filters)));
  };
  const calculateOrderByFilter = (columnName, previousOrderBy, sortState) => {
    const orderParts = [];

    // Handle payment_amount sorting
    const paymentState = sortState["payment_amount"];
    if (paymentState === 1) {
      orderParts.push("payment_amount DESC NULLS LAST");
    } else if (paymentState === 2) {
      orderParts.push("payment_amount ASC NULLS FIRST");
    }

    // Handle ended_at sorting
    const endedAtState = sortState["ended_at"];
    if (endedAtState === 1) {
      orderParts.push(
        "CASE WHEN ended_at IS NOT NULL THEN ended_at ELSE updated_at END DESC"
      );
    } else if (endedAtState === 2) {
      orderParts.push(
        "CASE WHEN ended_at IS NOT NULL THEN ended_at ELSE updated_at END ASC"
      );
    } else if (endedAtState === 0) {
      orderParts.push(
        "CASE WHEN ended_at IS NULL THEN 1 ELSE 0 END, ended_at DESC, updated_at DESC"
      );
    }

    // If both are 0, return empty string
    return orderParts.length > 0 ? orderParts.join(", ") : "";
  };

  const addFilterOrderHistory = (columnName, sortState) => {
    let filters = jobsHistoryFilters;
    filters.offset = 0; //restart from first page

    filters.orderBy = calculateOrderByFilter(
      columnName,
      filters.orderBy,
      sortState
    );

    setJobsHistoryFilters(JSON.parse(JSON.stringify(filters)));
  };

  const addFilterOrderQueue = (columnName, sortState) => {
    let filters = jobsQueueFilters;
    filters.offset = 0; //restart from first page
    if (sortState[columnName] === 0) {
      filters.orderBy =
        "CASE WHEN started_at IS NULL THEN 1 ELSE 0 END, started_at DESC, created_at DESC";
    } else if (sortState[columnName] === 1) {
      filters.orderBy = "started_at DESC NULLS LAST, created_at DESC";
    } else if (sortState[columnName] === 2) {
      filters.orderBy = "started_at ASC NULLS LAST, created_at ASC";
    }
    setJobsQueueFilters(JSON.parse(JSON.stringify(filters)));
  };

  const setJobNameQueue = (jobName) => {
    jobsQueueFilters.offset = 0; //restart from first page
    jobsQueueFilters.filters = jobsQueueFilters.filters.filter(
      (f) => f.field !== "name"
    );
    if (jobName && jobName.length > 0) {
      jobsQueueFilters.filters.push(
        props.api.createJobFilter(
          jobName.toLowerCase(),
          "name",
          CommonFilterCondition.LIKE
        )
      );
    }
    setJobsQueueFilters(JSON.parse(JSON.stringify(jobsQueueFilters)));
  };

  const setJobNameHistory = (jobName) => {
    jobsHistoryFilters.offset = 0; //restart from first page
    jobsHistoryFilters.filters = jobsHistoryFilters.filters.filter(
      (f) => f.field !== "name"
    );
    if (jobName && jobName.length > 0) {
      jobsHistoryFilters.filters.push(
        props.api.createJobFilter(
          jobName.toLowerCase(),
          "name",
          CommonFilterCondition.LIKE
        )
      );
    }
    setJobsHistoryFilters(JSON.parse(JSON.stringify(jobsHistoryFilters)));
  };

  const updateHistoryJobs = () => {
    props.api
      .getJobsFiltered(jobsHistoryFilters)
      .then((resp) => {
        setJobsHistory(resp);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const updateQueueJobs = () => {
    // if (pollingJobsTasksActive) {
    props.api
      .getJobsFiltered(jobsQueueFilters)
      .then((resp) => {
        setJobsQueue(resp);
        // const running = resp.data
        //   .filter((x) => x.state !== JobJobState.NEW)
        //   .map((j) => j.id);
        // if (running.length > 0) {
        //   props.api.getJobsTasks(running).then((resp) => {
        //     setRunningJobsTasks(resp.jobsRunningTasks);
        //     //if a job completed or failed we move from the queue to the history
        //     updateHistoryJobs();
        //   });
        // } else {
        //   updateHistoryJobs(); //last update of history
        //   setPollingJobsTasksActive(false);
        // }
      })
      .catch((err) => {
        console.error(err);
      });
    // }
  };
  const getJobDetails = (jobId, info) => {
    setIsLoadingJobDetails(true);
    props.api
      .getJobById(jobId)
      .then((responseJob) => {
        if (info === "history") {
          setJobHistoryDetails(responseJob);
          setIsLoadingJobDetails(false);
        } else {
          setJobQueueDetails(responseJob);
          setIsLoadingJobDetails(false);
        }
      })
      .catch((err) => {
        setIsLoadingJobDetails(false);
        console.error(err);
      });
  };

  //check every 30 seconds if the completed
  // useInterval(() => {
  //   updateQueueJobs();
  // }, POLLING_INTERVAL);

  useEffect(() => {
    updateHistoryJobs();
  }, [jobsHistoryFilters]);
  useEffect(() => {
    updateQueueJobs();
  }, [jobsQueueFilters]);

  return (
    <Box>
      <PageHeader title={"Jobs"}></PageHeader>

      <JobHistory
        jobsHistory={jobsHistory}
        jobsQueue={jobsQueue}
        isLoading={isLoadingJobDetails}
        setJobsHistoryFilters={setJobsHistoryFilters}
        setJobsQueueFilters={setJobsQueueFilters}
        runningJobsTasks={runningJobsTasks || {}}
        addFilterPageNumHistory={(pageNum) => addFilterPageNumHistory(pageNum)}
        addFilterPageNumQueue={(pageNum) => addFilterPageNumQueue(pageNum)}
        addFilterOrderHistory={(columnName, sortState) =>
          addFilterOrderHistory(columnName, sortState)
        }
        addFilterOrderQueue={(columnName, sortStates) =>
          addFilterOrderQueue(columnName, sortStates)
        }
        limitFilters={props.api.getDefaultLimitFilters()}
        abortJob={(jobId) => abortJob(props.api, jobId)}
        downloadResults={(jobId) => downloadResults(jobId)}
        copyJob={(jobId) => copyJob(props.api, jobId)}
        canAbort={canAbort}
        canEdit={canEdit}
        deleteJob={(jobId) => deleteJob(props.api, jobId)}
        runJob={(jobId) => runJob(props.api, jobId)}
        isComplete={isComplete}
        canDelete={canDelete}
        onChangeJobNameQueue={(jobName) => setJobNameQueue(jobName)}
        onChangeJobNameHistory={(jobName) => setJobNameHistory(jobName)}
        getJobDetailId={getJobDetails}
        onChangeJobDetail={setJobHistoryDetails}
        jobHistoryDetail={jobHistoryDetails}
        jobQueueDetails={jobQueueDetails}
        resultData={resultData}
      ></JobHistory>
    </Box>
  );
};

export default withApi(Encoding);
